import { memo, useEffect, useState } from 'react';
import { FeatureGroup, LayersControl, useMap } from 'react-leaflet';
import { useSelector } from 'react-redux';

import L from 'leaflet';
import isEmpty from 'lodash.isempty';

import { ConditionalWrapper, addStartEndIcons, getCoordinatesByAddress } from '../../helpers/map';

import { GEOMETRY_TYPE } from '../../constants/map';

import { useQuery } from '../../hooks/useQuery';
import { useUserCountry } from '../../hooks/useUserCountry';

import useFeatures from '../../components/Maps/useFeatures';

import BaseLayers from './BaseLayers';
import AppMapContainer from './Container';
import CustomLayers from './CustomLayers';
import MapTrackingFeature from './MapTrackingFeature';
import { LAYERS_CONTROL_POSITION, LAYER_NAME, ZOOM, ZOOM_MAX } from './constants';

const { Overlay } = LayersControl;

const Layers = memo(({ loading, grazing, onAnimalClick }) => {
  const map = useMap();

  const query = useQuery();
  const tab = query.get('tab');

  const { data } = useSelector(state => state.map);
  const { user } = useSelector(state => state.user);

  const [state, setState] = useState({
    features: { features: [] },
    features_master: {},
    features_sites: { features: [] },
    tracking: {}
  });

  const userDetectedAddress = useUserCountry();

  useEffect(() => {
    tick();
  }, [data, loading]);

  function calculateBounds(features, reverseCoordinates = true) {
    if (!features?.length) return;

    const bounds = L.latLngBounds();

    features.forEach(feature => {
      if (!feature.geometry || !feature.geometry.coordinates) return;

      const { type, coordinates } = feature.geometry;

      if (type === GEOMETRY_TYPE.POINT) {
        // Handle GeoJSON Point (with or without radius)
        const lat = reverseCoordinates ? coordinates[1] : coordinates[0];
        const lng = reverseCoordinates ? coordinates[0] : coordinates[1];
        const center = L.latLng(lat, lng);
        const radius = feature.properties?.radius;

        if (radius && radius > 0) {
          const circleBounds = center.toBounds(radius * 2);
          bounds.extend(circleBounds);
        } else {
          bounds.extend(center);
        }
      } else {
        // Handle Polygon / LineString / MultiPolygon / MultiLineString
        const flattenedCoords = type === GEOMETRY_TYPE.POLYGON ? coordinates.flat(1) : coordinates.flat(2);

        flattenedCoords.forEach(coordinate => {
          if (Array.isArray(coordinate) && coordinate.length === 2) {
            const lat = reverseCoordinates ? coordinate[1] : coordinate[0];
            const lng = reverseCoordinates ? coordinate[0] : coordinate[1];
            bounds.extend([lat, lng]);
          }
        });
      }
    });

    if (!bounds.isValid()) return;

    map.fitBounds(bounds, { padding: [10, 10], maxZoom: ZOOM_MAX });
  }

  const { farms, geofences, sites } = useFeatures({
    mapData: { farms: data?.farms, geofences: data?.geofences, sites: data?.sites }
  });

  async function tick() {
    if (isEmpty(data) || loading) return;

    const selectedItems = [];

    const pushFeature = (target, geo, color) => {
      if (geo?.geometry) {
        target.push({ ...geo, color });
      }
    };

    if (data.selected?.length) {
      data.selected?.forEach(item => {
        pushFeature(selectedItems, item.geo_json, item.color);
      });
    }

    // Tracking
    let updatedTrackingFeaturesList = [];
    let updatedTrackingFeatures = [];
    if (Array.isArray(data.tracking)) {
      for (const tr of data.tracking) {
        const validTrackingFeatures = tr?.features?.filter(t => t.geometry.coordinates.every(c => c !== null)) || [];

        updatedTrackingFeaturesList.push(addStartEndIcons({ ...tr, features: validTrackingFeatures }));
      }
    } else {
      const validTrackingFeatures =
        data.tracking?.features?.filter(t => t.geometry.coordinates.every(c => c !== null)) || [];
      updatedTrackingFeatures = addStartEndIcons({ ...data.tracking, features: validTrackingFeatures });
    }

    setState(prev => ({
      ...prev,
      tracking: {
        ...data.tracking,
        features: updatedTrackingFeaturesList?.length ? updatedTrackingFeaturesList.flat() : updatedTrackingFeatures
      },
      features_master: farms,
      features_sites: sites,
      features: geofences
    }));

    if (updatedTrackingFeaturesList?.length || updatedTrackingFeatures?.length) {
      calculateBounds(
        updatedTrackingFeaturesList?.length ? updatedTrackingFeaturesList.flat() : updatedTrackingFeatures,
        false
      );
    } else if (selectedItems?.length || farms?.features?.length) {
      calculateBounds(selectedItems?.length > 0 ? selectedItems : farms.features);
    } else {
      const address = user?.address ? `${user?.address?.country}, ${user?.address?.city}` : userDetectedAddress?.city;
      const coordinates = await getCoordinatesByAddress(address);
      coordinates && map?.setView(coordinates, ZOOM);
    }
  }

  return (
    <LayersControl position={LAYERS_CONTROL_POSITION}>
      <BaseLayers />
      <CustomLayers
        features={state.features}
        features_master={state.features_master}
        features_sites={state.features_sites}
        grazing={grazing}
        selected={data?.selected}
      />

      <ConditionalWrapper
        condition={!tab || tab === LAYER_NAME.LIVESTOCK.toLowerCase()}
        wrapper={children => (
          <Overlay checked name={LAYER_NAME.LIVESTOCK}>
            {children}
          </Overlay>
        )}>
        <FeatureGroup edit={{ edit: false, delete: false }}>
          {state.tracking &&
            state.tracking?.features?.length > 0 &&
            state.tracking?.features?.map(feature => {
              if (feature?.properties?.isVisible || feature?.geometry?.type === GEOMETRY_TYPE.LINE) {
                return (
                  <MapTrackingFeature
                    color={feature.properties?.color}
                    geometry={feature.geometry?.coordinates}
                    key={JSON.stringify(feature)}
                    properties={feature.properties}
                    type={feature.geometry?.type}
                    setAnimal={onAnimalClick}
                  />
                );
              }
            })}
        </FeatureGroup>
      </ConditionalWrapper>
    </LayersControl>
  );
});

function Map({ loading, grazing, onAnimalClick }) {
  return (
    <AppMapContainer>
      <Layers loading={loading} grazing={grazing} onAnimalClick={onAnimalClick} />
    </AppMapContainer>
  );
}

export default memo(Map);
